import * as React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowRightIcon, CalendarCheckIcon, CheckIcon, LogInIcon, MapPinIcon, UsersIcon } from 'lucide-react';

import { appIdentity } from '@/app/app-config';
import { useSession } from '@/components/auth/user-provider';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { PublicBrandBoundary, publicBrandSlug, publicPagesBranded, usePublicBrand } from '@/components/tenancy/public-brand';
import { useBrowserIdentity } from '@/lib/browser-identity';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';
import { publicFaqs, publicFeatures, publicNavigation, type PublicPage } from './public-site-content';

const pageTitles: Record<PublicPage, string> = {
  welcome: 'Welcome', features: 'Features', faq: 'Frequently asked questions'
};
const pageDescriptions: Record<PublicPage, string> = {
  welcome: 'Discover Commons Foundry: book shared spaces, plan time with colleagues and make more of the places you share.',
  features: 'Explore shared-space booking, floor maps, work plans, teams and workplace management in Commons Foundry.',
  faq: 'Find answers about Commons Foundry, work-account sign-in, bookings, privacy and organisation access.'
};

function BookingIllustration() {
  return (
    <figure className="bg-background relative rounded-2xl border p-5 shadow-sm sm:p-7">
      <figcaption className="mb-6 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold">A place for your day</span>
        <Badge variant="outline">Illustration</Badge>
      </figcaption>
      <div aria-hidden="true" className="bg-muted/40 grid grid-cols-3 gap-3 rounded-xl border p-4">
        <div className="bg-background col-span-2 flex min-h-24 items-center justify-center gap-3 rounded-lg border-2 border-dashed p-3">
          <UsersIcon className="text-muted-foreground size-5" />
          <span className="text-muted-foreground text-xs">Meet together</span>
        </div>
        <div className="bg-primary/10 flex items-center justify-center rounded-lg border"><MapPinIcon className="text-primary size-6" /></div>
        {['desk-a', 'desk-b', 'desk-c', 'desk-d', 'desk-e', 'desk-f'].map((id, index) => (
          <div key={id} className={cn('flex h-16 items-center justify-center rounded-lg border', index === 1 ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground')}>
            {index === 1 ? <CheckIcon className="size-5" /> : <span className="h-5 w-8 rounded border-2 border-current" />}
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-3">
        <span className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full"><CalendarCheckIcon className="size-5" aria-hidden /></span>
        <div><p className="text-sm font-semibold">Less searching. More doing.</p><p className="text-muted-foreground text-xs">Find a space that fits the way you work.</p></div>
      </div>
    </figure>
  );
}

/**
 * Anonymous, static product information. No inventory or people requests; the
 * only tenant request is the brand-safe look for `?org=<address>`, applied when
 * that organisation turns on public pages or browser identity.
 */
export function PublicSite({ page }: { page: PublicPage }) {
  const { search } = useLocation();
  const slug = React.useMemo(() => publicBrandSlug(search), [search]);
  return <PublicBrandBoundary slug={slug}><PublicSiteView page={page} /></PublicBrandBoundary>;
}

function PublicSiteView({ page }: { page: PublicPage }) {
  const { session, signIn } = useSession();
  const brand = usePublicBrand();
  const branded = publicPagesBranded(brand);
  const identityName = useBrowserIdentity().name;
  const siteName = branded && brand.name ? brand.name : appIdentity.name;
  const { ready, interactionError, localSignedOut, signOutError, logout } = useAuth();
  const location = useLocation();
  const [busy, setBusy] = React.useState(false);
  const [signInError, setSignInError] = React.useState<string | null>(null);
  const signInPending = React.useRef(false);
  const timer = React.useRef<number | null>(null);
  const mainRef = React.useRef<HTMLElement>(null);
  const previousPath = React.useRef(location.pathname);
  const hasSession = session.status === 'signedIn' || session.status === 'accessError';

  React.useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    const meta = description ?? document.createElement('meta');
    if (!description) { meta.name = 'description'; document.head.append(meta); }
    document.title = `${pageTitles[page]} · ${identityName}`;
    meta.content = pageDescriptions[page];
    return () => {
      document.title = previousTitle;
      if (previousDescription !== undefined) meta.content = previousDescription;
      else meta.remove();
    };
  }, [page, identityName]);

  React.useEffect(() => {
    if (previousPath.current !== location.pathname) {
      mainRef.current?.focus();
      previousPath.current = location.pathname;
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (page !== 'features' || !location.hash) return;
    const feature = publicFeatures.find((item) => `#${item.id}` === location.hash);
    if (feature) document.getElementById(feature.id)?.scrollIntoView?.({ block: 'start' });
  }, [page, location.hash]);

  React.useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  const handleSignIn = () => {
    if (signInPending.current || !ready || session.status !== 'signedOut') return;
    signInPending.current = true;
    setBusy(true);
    setSignInError(null);
    try {
      // Keep the existing sign-in call on the click gesture for embedded handoff.
      signIn();
      timer.current = window.setTimeout(() => {
        signInPending.current = false;
        setBusy(false);
      }, 1500);
    } catch {
      signInPending.current = false;
      setBusy(false);
      setSignInError('Sign-in could not start. Please try again or contact your organisation’s IT support team.');
    }
  };

  const applicationAction = (large = false) => hasSession ? (
    <Button asChild size={large ? 'lg' : 'sm'} className="gap-2">
      <Link to="/">Open application<ArrowRightIcon className="size-4" aria-hidden /></Link>
    </Button>
  ) : (
    <Button type="button" size={large ? 'lg' : 'sm'} onClick={handleSignIn} disabled={busy || !ready || session.status === 'loading'} className="gap-2">
      <LogInIcon className="size-4" aria-hidden />
      {busy ? 'Opening sign-in…' : session.status === 'loading' || !ready ? 'Checking sign-in…' : 'Sign in'}
    </Button>
  );

  return (
    <div className="bg-background text-foreground flex min-h-svh flex-col">
      <a href="#public-main" className="bg-background ring-ring sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-4 focus:py-2 focus:ring-2">Skip to main content</a>
      <header className="bg-background/95 border-border/70 sticky top-0 z-20 border-b backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-3 px-4 py-4 sm:px-6">
          <Link to="/welcome" className="ring-ring flex min-w-0 items-center gap-2 rounded-md focus-visible:ring-2">
            {branded && brand.logo
              ? <img src={brand.logo.url} alt="" data-testid="public-brand-logo" className="h-9 w-auto max-w-32 shrink-0 object-contain" />
              : <img src={appIdentity.iconPath} alt="" className="size-9 shrink-0 rounded-lg" />}
            <span className="text-sm font-bold tracking-tight sm:text-base">{siteName}</span>
          </Link>
          <div className="flex items-center gap-2 sm:order-last"><ThemeToggle />{applicationAction()}</div>
          <nav aria-label="Public navigation" className="order-last flex w-full gap-1 sm:order-none sm:mx-auto sm:w-auto">
            {publicNavigation.map((item) => (
              <NavLink key={item.path} to={item.path} aria-current={(item.path === '/welcome' && page === 'welcome') ? 'page' : undefined}
                className={({ isActive }) => cn('ring-ring rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent focus-visible:ring-2', isActive || (item.path === '/welcome' && page === 'welcome') ? 'bg-accent text-foreground' : 'text-muted-foreground')}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <main id="public-main" ref={mainRef} tabIndex={-1} className="mx-auto w-full max-w-6xl flex-1 space-y-16 px-4 py-10 outline-none sm:px-6 sm:py-16">
        {localSignedOut ? <section aria-label="Sign-out status" className="bg-muted/50 space-y-3 rounded-lg border p-4 text-sm">
          <p role="status">You are signed out of this panel. Microsoft sign-out is unconfirmed.</p>
          <p className="text-muted-foreground">Finish sign-out in the new tab. If no tab appears, allow new tabs and retry. This panel stays signed out. To sign in again, use Sign in and continue in the new tab.</p>
          {signOutError ? <p role="alert">{signOutError}</p> : null}
          <Button type="button" variant="outline" onClick={logout}>Retry sign-out</Button>
        </section> : null}
        {signInError || interactionError ? <div role="alert" className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border p-4 text-sm">{signInError ?? interactionError}</div> : null}

        {page === 'welcome' ? (
          <>
            <section aria-labelledby="welcome-heading" className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
              <div className="space-y-6">
                <Badge variant="secondary">Shared places. Better used.</Badge>
                <h1 id="welcome-heading" className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl">Make room for a<br className="hidden sm:block" /> better working day.</h1>
                <p className="text-muted-foreground max-w-xl text-lg leading-relaxed">{appIdentity.description}</p>
                <div className="flex flex-wrap gap-3">
                  {applicationAction(true)}
                  <Button asChild size="lg" variant="outline"><Link to="/features">Explore the features<ArrowRightIcon className="ml-2 size-4" aria-hidden /></Link></Button>
                </div>
                <p className="text-muted-foreground text-sm">Use your Microsoft work account. Your organisation provides access.</p>
              </div>
              <BookingIllustration />
            </section>

            <section aria-labelledby="purpose-heading" className="bg-muted/50 rounded-2xl border p-6 sm:p-10">
              <p className="text-muted-foreground mb-3 text-xs font-semibold tracking-widest uppercase">Why Commons Foundry</p>
              <h2 id="purpose-heading" className="max-w-3xl text-2xl font-bold tracking-tight sm:text-3xl">{appIdentity.tagline}</h2>
              <p className="text-muted-foreground mt-4 max-w-3xl leading-relaxed">A desk for focused work. A room to bring people together. A clearer picture of who is coming in. Bring everyday workplace decisions into one place, so you can spend less time organising your day and more time making it count.</p>
            </section>

            <section aria-labelledby="possibilities-heading" className="space-y-6">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="space-y-2"><h2 id="possibilities-heading" className="text-2xl font-bold tracking-tight sm:text-3xl">One place to plan, book and connect.</h2><p className="text-muted-foreground">Start with what you need today. Discover what else is possible.</p></div>
                <Button asChild variant="ghost"><Link to="/features">View all features<ArrowRightIcon className="ml-2 size-4" aria-hidden /></Link></Button>
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                {publicFeatures.slice(0, 3).map((feature) => (
                  <Card key={feature.id} className="shadow-none">
                    <CardHeader><feature.icon className="mb-3 size-6" aria-hidden /><CardTitle className="text-lg">{feature.title}</CardTitle></CardHeader>
                    <CardContent className="text-muted-foreground text-sm leading-relaxed">{feature.summary} <Link className="text-foreground rounded-sm underline underline-offset-4 hover:no-underline focus-visible:ring-2" to={`/features#${feature.id}`}>Learn more<span className="sr-only"> about {feature.title.toLowerCase()}</span></Link></CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section aria-labelledby="how-heading" className="space-y-6">
              <h2 id="how-heading" className="text-2xl font-bold tracking-tight sm:text-3xl">Your next office day, in three steps.</h2>
              <ol className="grid gap-6 md:grid-cols-3">
                {[
                  { title: 'Sign in and choose your workplace', text: 'Use your authorised work account, then choose the organisation or entity you want to use.' },
                  { title: 'Find the right place and time', text: 'Choose a location, date and resource. Check the details and any rules before you book.' },
                  { title: 'Make it yours', text: 'Confirm your reservation. Review your day in My Bookings and follow any arrival or check-in instructions.' }
                ].map((step, index) => (
                  <li key={step.title} className="border-border space-y-3 border-t pt-5">
                    <span className="text-muted-foreground text-sm font-semibold tabular-nums">0{index + 1}</span>
                    <h3 className="font-semibold">{step.title}</h3><p className="text-muted-foreground text-sm leading-relaxed">{step.text}</p>
                  </li>
                ))}
              </ol>
            </section>
          </>
        ) : page === 'features' ? (
          <>
            <section className="max-w-3xl space-y-5">
              <Badge variant="secondary">Built around the working day</Badge>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">More from the places you share.</h1>
              <p className="text-muted-foreground text-lg leading-relaxed">From finding a desk to understanding a whole workplace, Commons Foundry brings shared-space booking and planning together.</p>
              <p className="bg-muted/50 text-muted-foreground rounded-lg border p-4 text-sm">Your organisation chooses which modules, resources and connections are available. Some features require additional permissions or setup.</p>
            </section>
            <section aria-label="Application features" className="grid gap-5 md:grid-cols-2">
              {publicFeatures.map((feature) => (
                <Card key={feature.id} id={feature.id} className="scroll-mt-40 shadow-none sm:scroll-mt-28">
                  <CardHeader><span className="bg-muted mb-3 flex size-12 items-center justify-center rounded-xl"><feature.icon className="size-6" aria-hidden /></span><CardTitle className="text-xl">{feature.title}</CardTitle></CardHeader>
                  <CardContent className="space-y-5"><p className="text-muted-foreground text-sm leading-relaxed">{feature.details}</p>
                    <ul className="space-y-2">{feature.items.map((item) => <li key={item} className="flex gap-2 text-sm"><CheckIcon className="mt-0.5 size-4 shrink-0" aria-hidden />{item}</li>)}</ul>
                  </CardContent>
                </Card>
              ))}
            </section>
          </>
        ) : (
          <>
            <section className="max-w-3xl space-y-5">
              <Badge variant="secondary">A little help getting started</Badge>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">Frequently asked questions.</h1>
              <p className="text-muted-foreground text-lg leading-relaxed">Everything from your first sign-in to finding a space for your next working day.</p>
            </section>
            <section aria-label="Frequently asked questions" className="max-w-3xl">
              <Accordion type="multiple" className="rounded-xl border px-5 sm:px-7">
                {publicFaqs.map((faq) => <AccordionItem key={faq.id} value={faq.id}><AccordionTrigger className="py-5 text-base">{faq.question}</AccordionTrigger><AccordionContent className="text-muted-foreground leading-relaxed">{faq.answer}</AccordionContent></AccordionItem>)}
              </Accordion>
            </section>
            <aside className="bg-muted/50 max-w-3xl rounded-xl border p-6"><h2 className="text-lg font-semibold">Need a hand with your workplace?</h2><p className="text-muted-foreground mt-2 text-sm leading-relaxed">Your organisation’s workplace, facilities or IT support team can help with account access, booking policies and resource availability.</p></aside>
          </>
        )}

        <section id="sign-in-info" aria-labelledby="signin-heading" className="bg-primary text-primary-foreground scroll-mt-40 rounded-2xl p-6 sm:p-10">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-xl space-y-3">
              <h2 id="signin-heading" className="text-2xl font-bold tracking-tight">Ready to plan your day?</h2>
              <p className="text-sm leading-relaxed opacity-90">Sign in with your Microsoft work account to see the workplaces you can access. New here? Ask your organisation’s administrator to arrange access.</p>
            </div>
            <div className="bg-background text-foreground flex shrink-0 flex-col items-start gap-3 rounded-xl p-5">
              {applicationAction(true)}
              <Link to="/faq" className="text-muted-foreground ring-ring rounded-sm text-sm underline underline-offset-4 hover:text-foreground focus-visible:ring-2">Read the sign-in and access FAQ</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-muted/30 border-t">
        <div className="mx-auto flex max-w-6xl flex-col justify-between gap-6 px-4 py-8 sm:flex-row sm:px-6">
          <div className="max-w-sm space-y-2"><p className="text-sm font-bold">{appIdentity.name}</p><p className="text-muted-foreground text-xs leading-relaxed">Make more of the places we share. Workplace information and bookings require authorised access.</p></div>
          <nav aria-label="Footer navigation" className="flex flex-wrap items-start gap-5">{publicNavigation.map((item) => <Link key={item.path} to={item.path} className="text-muted-foreground ring-ring rounded-sm text-sm hover:text-foreground hover:underline focus-visible:ring-2">{item.label}</Link>)}</nav>
        </div>
      </footer>
    </div>
  );
}
